import { zodResolver } from '@hookform/resolvers/zod';
import { useAcceptTransfusions } from '@hooks/register/screening/use-accept-transfusions';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { useSession } from '@hooks/security/use-session';
import { acceptTransfusionsSchema } from '@views/register/screening/accept-transfusions.schema';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import { asStringBool } from '@/utils/parse';
import type { IAcceptTransfusionsFormValues, IAcceptTransfusionsModel, IAcceptTransfusionsSubmitResult } from '@/types/screening/accept-transfusions';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

const AcceptTransfusions = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAcceptTransfusions(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AcceptTransfusionsContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IAcceptTransfusionsContentProps {
    model: IAcceptTransfusionsModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAcceptTransfusionsFormValues) => Promise<IAcceptTransfusionsSubmitResult>;
}

const AcceptTransfusionsContent = ({ model, isSubmitting, submitError, onSubmit }: IAcceptTransfusionsContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IAcceptTransfusionsFormValues, IAcceptTransfusionsSubmitResult>({
        resolver: zodResolver(acceptTransfusionsSchema),
        defaultValues: {
            willAcceptTransfusions: asStringBool(model.willAcceptTransfusions),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        formState: { errors },
    } = form;

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.AcceptTransfusions}
            title={t('DashLabel_ProgressBloodTransfusions', 'Blood Transfusions')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t(
                        'DashLabel_AreYouWillingToAcceptBloodTransfusionsIfNecessary',
                        'Are you willing to receive a blood transfusion if it is needed in an emergency',
                    )}
                    ?<FieldError message={errors.willAcceptTransfusions?.message} />
                </label>

                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input
                            type="radio"
                            value="1"
                            {...register('willAcceptTransfusions')}
                        />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input
                            type="radio"
                            value="0"
                            {...register('willAcceptTransfusions')}
                        />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>
        </StepFormLayout>
    );
};

export default AcceptTransfusions;
